import { useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { HelpCircle, MessageCircle, Phone, Mail, ChevronRight, Search } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'

export function SupportPage() {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')

  const categories = [
    { id: 'all', label: t('support.all') },
    { id: 'account', label: t('support.account') },
    { id: 'payments', label: t('support.payments') },
    { id: 'security', label: t('support.security') },
    { id: 'technical', label: t('support.technical') }
  ]

  const faqs = [
    {
      id: '1',
      category: 'account',
      question: t('support.faq1.question'),
      answer: t('support.faq1.answer')
    },
    {
      id: '2',
      category: 'payments',
      question: t('support.faq2.question'),
      answer: t('support.faq2.answer')
    },
    {
      id: '3',
      category: 'security',
      question: t('support.faq3.question'),
      answer: t('support.faq3.answer')
    },
    {
      id: '4',
      category: 'payments',
      question: t('support.faq4.question'),
      answer: t('support.faq4.answer')
    },
    {
      id: '5',
      category: 'technical',
      question: t('support.faq5.question'),
      answer: t('support.faq5.answer')
    }
  ]

  const contactMethods = [
    {
      icon: MessageCircle,
      title: t('support.onlineChat'),
      subtitle: t('support.responseTime'),
      action: () => {} // Open chat
    },
    {
      icon: Phone,
      title: t('support.phone'),
      subtitle: '+239 123 456 789',
      action: () => {} // Call phone
    },
    {
      icon: Mail,
      title: t('support.email'),
      subtitle: 'suporte@djelo.st',
      action: () => {} // Send email
    }
  ]

  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  return (
    <div className="min-h-screen bg-background-light">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 p-4">
        <h1 className="text-2xl font-bold text-text-primary mb-2">{t('navigation.support')}</h1>
        <p className="text-text-secondary">{t('support.subtitle')}</p>
      </div>

      {/* Search */}
      <div className="p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative mb-4"
        >
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-text-secondary" />
            <Input
              type="text"
              placeholder={t('support.searchHelp')}
              value={searchTerm}
              onChange={setSearchTerm}
              className="pl-10"
            />
        </motion.div>

        {/* Categories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex items-center space-x-2 overflow-x-auto mb-6"
        >
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category.id)}
              className="whitespace-nowrap"
            >
              {category.label}
            </Button>
          ))}
        </motion.div>

        {/* Contact Methods */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6"
        >
          <h2 className="text-lg font-semibold text-text-primary mb-4">{t('support.contactUs')}</h2>
          <div className="space-y-3">
            {contactMethods.map((method, index) => {
              const IconComponent = method.icon
              
              return (
                <motion.button
                  key={method.title}
                  onClick={method.action}
                  className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <IconComponent className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="font-medium text-text-primary">{method.title}</h3>
                    <p className="text-sm text-text-secondary">{method.subtitle}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-text-secondary" />
                </motion.button>
              )
            })}
          </div>
        </motion.div>

        {/* FAQ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100"
        >
          <div className="p-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-text-primary">{t('support.faq')}</h2>
          </div>
          
          <div className="divide-y divide-gray-100">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="p-4"
                >
                  <h3 className="font-medium text-text-primary mb-2">{faq.question}</h3>
                  <p className="text-sm text-text-secondary">{faq.answer}</p>
                </motion.div>
              ))
            ) : (
              <div className="p-8 text-center">
                <HelpCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  {t('support.noQuestionsFound')}
                </h3>
                <p className="text-text-secondary">
                  {t('support.adjustFilters')}
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  )
}




